import moment, { Moment } from 'moment';

const oneDay = 60 * 60 * 24 * 1000;
type time = { hours?: number; minutes?: number };

export const todayTimestamp =
  Date.now() - (Date.now() % oneDay) + new Date().getTimezoneOffset() * 1000 * 60;
export const uniqueId = () => `_${Math.random().toString(36).slice(2, 11)}`;

export const incrementList = (year: number): number[] => {
  let index = 9;
  const list: number[] = [];
  let lookAhead = year + 4;
  while (index > 0) {
    index -= 1;
    list.push(lookAhead);
    lookAhead -= 1;
  }
  return list.reverse();
};
export const fillRange = (start: number, end: number) =>
  new Array(end - start + 1).fill(0).map((item, index) => start + index);

export const daysMap = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];
export const monthMap = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const getNumberOfDays = (year: number, month: number) =>
  40 - new Date(year, month, 40).getDate();

export const numberOfDays = (startingDate: Date) => {
  let days = getNumberOfDays(startingDate.getFullYear(), startingDate.getMonth());
  const daysArray = [];
  while (days > 0) {
    daysArray.push(days);
    days -= 1;
  }
  return daysArray.reverse();
};

const getDayDetails = (arguments_: {
  index: number;
  numberOfDays: number;
  firstDay: number;
  year: number;
  month: number;
}) => {
  const date = arguments_.index - arguments_.firstDay;
  const day = arguments_.index % 7;
  let previousMonth = arguments_.month - 1;
  let currentYear = arguments_.year;
  if (previousMonth < 0) {
    previousMonth = 11;
    currentYear -= 1;
  }
  const previousMonthNumberOfDays = getNumberOfDays(currentYear, previousMonth);
  const previousDate =
    (date < 0 ? previousMonthNumberOfDays + date : date % arguments_.numberOfDays) + 1;
  const month = (date < 0 ? -1 : date >= arguments_.numberOfDays) ? 1 : 0;
  const timestamp = new Date(arguments_.year, arguments_.month, previousDate).getTime();
  return {
    date: previousDate,
    day,
    month,
    timestamp,
    dayString: daysMap[day],
  };
};

export const getMonthDetails = (year: number, month: number) => {
  const firstDay = new Date(year, month).getDay();
  const numberOfDaysInYear = getNumberOfDays(year, month);
  const monthArray = [];
  const rows = 6;
  let currentDay = null;
  let index = 0;
  const cols = 7;

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      currentDay = getDayDetails({
        index,
        numberOfDays: numberOfDaysInYear,
        firstDay,
        year,
        month,
      });
      monthArray.push(currentDay);
      index += 1;
    }
  }
  return monthArray;
};

export const isCurrentDay = (day: { month?: number; date?: number; timestamp?: number }) =>
  day.timestamp === todayTimestamp;

export const isFutureDate = (day: { month?: number; date?: number; timestamp?: number }) =>
  (day.timestamp && day.timestamp > todayTimestamp) || day.month !== 0;

export const isSelectedDay = (
  day: { month?: number; date?: number; timestamp?: number },
  selectedDay: number | undefined,
) => {
  if (selectedDay && day.timestamp) {
    const selected = new Date(selectedDay);
    const toCheck = new Date(day.timestamp);
    return (
      selected.getFullYear() === toCheck.getFullYear() &&
      selected.getMonth() === toCheck.getMonth() &&
      selected.getDate() === toCheck.getDate()
    );
  }
  return null;
};

export const getMonthString = (month: number) =>
  monthMap[Math.max(Math.min(11, month), 0)] || 'Month';

export const onDateClick = (
  day: { month?: number; date?: number; timestamp?: number },
  setSelectedDay: Function,
) => {
  setSelectedDay(day.timestamp);
};

export const setYear = (
  offset: any,
  year: number,
  month: number,
  setMonthDetails: Function,
  setYearState: Function,
) => {
  const newYear = year + offset;
  setYearState(newYear);
  setMonthDetails(getMonthDetails(newYear, month));
};

export const setMonth = (
  offset: any,
  year: number,
  month: number,
  setMonthState: Function,
  setYearState: Function,
) => {
  let newMonth = month + offset;
  let newYear = year;
  if (newMonth === -1) {
    newMonth = 11;
    newYear = year - 1;
  } else if (newMonth === 12) {
    newMonth = 0;
    newYear = year + 1;
  }
  setYearState(newYear);
  setMonthState(newMonth);
};

export const isInsideRange = (
  day: number | undefined,
  selectedDay: number | undefined,
  otherSelection: number | undefined,
) => {
  if (otherSelection && day && selectedDay) {
    const newOtherSelection = new Date(new Date(otherSelection).toDateString()).valueOf();
    if (
      (day > selectedDay && day < newOtherSelection) ||
      (day < selectedDay && day > newOtherSelection)
    )
      return true;
  }
  return false;
};

const addTime = (date: number, timeReference: time, oldestDate: boolean) => {
  const localMergedDate = new Date(date);
  const mergedDate = new Date(
    Date.UTC(localMergedDate.getFullYear(), localMergedDate.getMonth(), localMergedDate.getDate()),
  );
  const localHours = localMergedDate.getUTCHours();
  mergedDate.setUTCHours(oldestDate ? 0 : localHours);
  const localMins = localMergedDate.getUTCMinutes();
  const localSeconds = localMergedDate.getUTCSeconds();
  mergedDate.setUTCMinutes(oldestDate ? 0 : localMins);
  mergedDate.setUTCSeconds(oldestDate ? 0 : localSeconds);
  if (timeReference.minutes) mergedDate.setUTCMinutes(timeReference.minutes);
  if (timeReference.hours) mergedDate.setUTCHours(timeReference.hours);
  return mergedDate;
};

export const formatAndMerge = (
  firstSelectedTime: time,
  secondSelectedTime: time,
  firstSelected: number | undefined,
  secondSelected: number | undefined,
  blockFutureDates: boolean | undefined,
) => {
  let selection1: Moment;
  let selection2: Moment;
  const today = moment(new Date());
  if (firstSelected && secondSelected) {
    selection1 = moment.utc(
      addTime(firstSelected, firstSelectedTime, firstSelected < secondSelected),
    );
    selection2 = moment.utc(
      addTime(secondSelected, secondSelectedTime, firstSelected >= secondSelected),
    );
    if ((blockFutureDates) && (today < moment(firstSelected) || today < moment(secondSelected))) {
      return null;
    }
    if (selection1.isAfter(selection2)) {
      return [selection2.startOf("minute").toISOString(), selection1.startOf("minute").toISOString()];
    }
    return [selection1.startOf("minute").toISOString(), selection2.startOf("minute").toISOString()];
  }
  return null;
};

type TimeFormat = {
  hours: number;
  minutes: number;
};
type DateFlipper = {
  firstSelected?: number;
  setFirstSelected: Function;
  secondSelected?: number;
  setSecondSelected: Function;
  firstSelectedTime: TimeFormat;
  secondSelectedTime: TimeFormat;
  setFirstSelectedTime: Function;
  setSecondSelectedTime: Function;
};

type DateFlipperReturn = {
  left: {
    selected?: number;
    setSelected: Function;
    time: TimeFormat;
    setTime: Function;
  };
  right: {
    selected?: number;
    setSelected: Function;
    time: TimeFormat;
    setTime: Function;
  };
};

export const DateDropDownFlipper = ({
  firstSelected,
  setFirstSelected,
  secondSelected,
  setSecondSelected,
  firstSelectedTime,
  secondSelectedTime,
  setFirstSelectedTime,
  setSecondSelectedTime,
}: DateFlipper): DateFlipperReturn => {
  const defaultValue = {
    left: {
      selected: firstSelected,
      setSelected: setFirstSelected,
      time: firstSelectedTime,
      setTime: setFirstSelectedTime,
    },
    right: {
      selected: secondSelected,
      setSelected: setSecondSelected,
      time: secondSelectedTime,
      setTime: setSecondSelectedTime,
    },
  };
  return defaultValue;
};
